// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import type { Theme } from "@/types/view/theme";

export const THEMES: Theme[] = [
  {
    name: "DevGuard",
    description: "Default — amber on deep navy",
    swatch: "#f5b529",
    dark: true,
    style: {
      shadowPreset: "Default",
      fontFamily: "",
      displayFont: "Lexend, sans-serif",
      fontSize: "16px",
      letterSpacing: "0em",
      lineHeight: "",
    },
    vars: {
      "--background": "213 27% 7%",
      "--foreground": "0 0% 98%",
      "--card": "215 22% 11%",
      "--card-foreground": "0 0% 98%",
      "--popover": "213 31% 7%",
      "--popover-foreground": "0 0% 98%",
      "--primary": "43 95% 56%",
      "--primary-foreground": "0 0% 2%",
      "--secondary": "215 27.9% 16.9%",
      "--secondary-foreground": "210 20% 98%",
      "--muted": "213 15% 15%",
      "--muted-foreground": "212 9% 59%",
      "--accent": "214 21% 21%",
      "--accent-foreground": "0 0% 98%",
      "--border": "215 22% 15%",
      "--input": "215 22% 15%",
      "--ring": "43 95% 40%",
      "--radius": "0.5rem",
      "--sidebar-background": "213 31% 7%",
      "--sidebar-foreground": "240 4.8% 95.9%",
      "--sidebar-accent": "215 22% 15%",
      "--sidebar-accent-foreground": "0 0% 98%",
      "--sidebar-border": "215 22% 15%",
      "--header-background": "225 66% 2%",
      "--header-foreground": "0 0% 100%",
      "--header-muted-foreground": "211 14% 55%",
      "--footer-background": "213 27% 7%",
      "--footer-foreground": "0 0% 98%",
      "--footer-muted-foreground": "213 15% 55%",
      "--success": "142 71% 50%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 92% 55%",
      "--warning-foreground": "0 0% 5%",
      "--info": "199 89% 48%",
      "--info-foreground": "0 0% 100%",
      "--severity-critical": "0 84% 60%",
      "--severity-high": "25 95% 58%",
      "--severity-medium": "45 93% 55%",
      "--severity-low": "142 71% 50%",
      "--success-muted": "142 30% 18%",
      "--success-hover": "142 71% 43%",
      "--warning-muted": "38 40% 18%",
      "--warning-border": "38 55% 35%",
      "--info-muted": "199 40% 18%",
      "--destructive-muted": "350 35% 18%",
      "--accent-muted": "215 22% 20%",
      "--accent-ring": "215 22% 32%",
      "--severity-critical-muted": "0 35% 18%",
      "--severity-high-muted": "25 40% 18%",
      "--severity-medium-muted": "45 35% 18%",
      "--severity-low-muted": "142 30% 18%",
      "--grid-line-color": "215 22% 15% / 0.6",
      "--link": "43 95% 65%",
    },
  },
  {
    name: "Glacier",
    description: "Crisp white — ice blue & frost",
    swatch: "#0ea5e9",
    dark: false,
    style: {
      fontFamily: "",
      displayFont: "",
      fontSize: "16px",
      letterSpacing: "",
      lineHeight: "",
      shadowPreset: "Default",
    },
    vars: {
      "--background": "200 20% 98%",
      "--foreground": "206 59% 16%",
      "--card": "0 0% 100%",
      "--card-foreground": "205 30% 10%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "205 30% 10%",
      "--primary": "43 96% 56%",
      "--primary-foreground": "206 59% 16%",
      "--secondary": "200 15% 92%",
      "--secondary-foreground": "205 30% 10%",
      "--muted": "200 12% 94%",
      "--muted-foreground": "200 12% 46%",
      "--accent": "212 31% 88%",
      "--accent-foreground": "205 30% 10%",
      "--border": "200 15% 88%",
      "--input": "200 15% 88%",
      "--ring": "198 82% 44%",
      "--radius": "0.5rem",
      "--sidebar-background": "200 18% 96%",
      "--sidebar-foreground": "205 30% 10%",
      "--sidebar-accent": "200 15% 90%",
      "--sidebar-accent-foreground": "205 30% 10%",
      "--sidebar-border": "200 15% 86%",
      "--header-background": "205 60% 16%",
      "--header-foreground": "0 0% 100%",
      "--header-muted-foreground": "205 35% 55%",
      "--footer-background": "200 20% 98%",
      "--footer-foreground": "205 30% 10%",
      "--footer-muted-foreground": "200 12% 48%",
      "--success": "142 64% 35%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 75% 36%",
      "--warning-foreground": "0 0% 5%",
      "--info": "199 89% 40%",
      "--info-foreground": "0 0% 100%",
      "--severity-critical": "0 84% 55%",
      "--severity-high": "25 77% 47%",
      "--severity-medium": "45 79% 44%",
      "--severity-low": "142 71% 45%",
      "--success-muted": "142 50% 90%",
      "--success-hover": "142 65% 38%",
      "--warning-muted": "38 80% 90%",
      "--warning-border": "38 51% 71%",
      "--info-muted": "199 70% 90%",
      "--destructive-muted": "350 80% 93%",
      "--accent-muted": "198 40% 88%",
      "--accent-ring": "198 40% 76%",
      "--severity-critical-muted": "0 70% 92%",
      "--severity-high-muted": "25 80% 90%",
      "--severity-medium-muted": "45 80% 88%",
      "--severity-low-muted": "142 50% 90%",
      "--grid-line-color": "200 15% 88% / 0.6",
      "--link": "199 89% 34%",
      "--destructive": "0 100% 35%",
      "--destructive-foreground": "0 0% 100%",
    },
  },
];
